import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Check, Loader2, FileText, Archive, Star, Trash2 } from 'lucide-react';
import { QuoteCard } from './QuoteCard';

export const QuotesList = ({
  quotes,
  emptyIcon: EmptyIcon = FileText,
  emptyTitle = 'No quotes found',
  emptyDescription = '',
  loading = false,
  expandedQuote,
  onToggleExpand,
  onApproveQuote,
  onRejectQuote,
  onRequestInspection,
  onEditQuote,
  onArchiveQuote,
  onDeleteQuote,
  onRestoreQuote,
  actionLoading,
  showStatus = false,
  selectedQuotes,
  onSelectQuote,
  onSelectAll,
  onBulkAction,
  bulkLoading,
  bulkActions = ['highlight', 'archive', 'delete'],
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (quotes.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <EmptyIcon className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h3 className="text-xl font-semibold mb-2 font-['Outfit']">{emptyTitle}</h3>
          {emptyDescription && <p className="text-muted-foreground text-sm">{emptyDescription}</p>}
        </CardContent>
      </Card>
    );
  }

  const allSelected = quotes.length > 0 && quotes.every(q => selectedQuotes.has(q.id));

  // Determine if a quote should get action buttons based on status
  const canAct = (quote) => !showStatus || quote.status === 'pending_review';

  return (
    <div className="space-y-4">
      {/* Bulk Action Bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => onSelectAll(quotes)}
            className="h-4 w-4 rounded border-gray-300 accent-primary"
            data-testid="select-all-checkbox"
          />
          <span className="font-medium">Select All</span>
        </label>
        {selectedQuotes.size > 0 && (
          <>
            <span className="text-xs text-muted-foreground">{selectedQuotes.size} selected</span>
            <div className="flex items-center gap-2 ml-auto">
              {bulkActions.includes('highlight') && (
                <Button size="sm" variant="outline" onClick={() => onBulkAction('highlight')} disabled={bulkLoading} className="text-xs gap-1" data-testid="bulk-highlight">
                  <Star className="w-3.5 h-3.5" /> Highlight
                </Button>
              )}
              {bulkActions.includes('unhighlight') && (
                <Button size="sm" variant="outline" onClick={() => onBulkAction('unhighlight')} disabled={bulkLoading} className="text-xs gap-1">
                  <Star className="w-3.5 h-3.5 opacity-40" /> Unhighlight
                </Button>
              )}
              {bulkActions.includes('archive') && (
                <Button size="sm" variant="outline" onClick={() => onBulkAction('archive')} disabled={bulkLoading} className="text-xs gap-1" data-testid="bulk-archive">
                  <Archive className="w-3.5 h-3.5" /> Archive
                </Button>
              )}
              {bulkActions.includes('delete') && (
                <Button size="sm" variant="destructive" onClick={() => onBulkAction('delete')} disabled={bulkLoading} className="text-xs gap-1" data-testid="bulk-delete">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {quotes.map((quote) => (
        <QuoteCard
          key={quote.id}
          quote={quote}
          expanded={expandedQuote === quote.id}
          onToggle={() => onToggleExpand(quote.id)}
          onApprove={canAct(quote) && onApproveQuote ? () => onApproveQuote(quote.id) : null}
          onReject={canAct(quote) && onRejectQuote ? (reason) => onRejectQuote(quote.id, reason) : null}
          onRequestInspection={canAct(quote) && onRequestInspection ? () => onRequestInspection(quote.id) : null}
          onEdit={canAct(quote) && onEditQuote ? () => onEditQuote(quote) : null}
          onArchive={onArchiveQuote ? () => onArchiveQuote(quote.id) : null}
          onDelete={onDeleteQuote ? () => onDeleteQuote(quote.id) : null}
          onRestore={onRestoreQuote ? () => onRestoreQuote(quote.id) : null}
          loading={actionLoading === quote.id}
          showStatus={showStatus}
          selected={selectedQuotes.has(quote.id)}
          onSelect={onSelectQuote}
        />
      ))}
    </div>
  );
};
